import { Injector } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { CreateAccount } from 'src/app/accounts/interfaces/accounts.types';
import { AccountsService } from 'src/app/accounts/services/accounts.service';
import { QuestionnaireService } from 'src/app/healthscore/services/questionnaire.service';
import { OnboardingIntentStep } from 'src/app/shared/enums/intent.enum';
import { IntentStrategy, IntentStrategyConfig } from './intent.strategy';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';

export class GeneralIntentStrategy implements IntentStrategy {
  router: Router;
  route: ActivatedRoute;
  questionnaireService: QuestionnaireService;
  accountService: AccountsService;

  constructor(private injector: Injector) {
    this.router = this.injector.get(Router);
    this.route = this.injector.get(ActivatedRoute);
    this.questionnaireService = this.injector.get(QuestionnaireService);
    this.accountService = this.injector.get(AccountsService);
  }

  public getConfig(): IntentStrategyConfig {
    return {
      [OnboardingIntentStep.Caution]: {
        paragraph1: 'ACCOUNTS.CAUTION.GENERAL.PARAGRAPH_1',
        paragraph2: 'ACCOUNTS.CAUTION.GENERAL.PARAGRAPH_2',
      },
      [OnboardingIntentStep.Complete]: {
        title: 'ONBOARDING.COMPLETE.COMPLETE',
        description: 'ONBOARDING.COMPLETE.YOUR_REGISTRATION_IS',
      },
    };
  }

  public onRegisterCreateAccount({
    skipEmailVerification,
    ...rest
  }: CreateAccount): Observable<any> {
    return this.accountService.create(rest).pipe(
      tap(() => {
        const queryParams = skipEmailVerification ? { skip: true } : {};

        this.router.navigate(['/accounts/newsletters'], {
          // masking query parameter which is used for skipping verified guard -> Questioner onboarding
          queryParams,
          replaceUrl: true,
        });
      })
    );
  }

  public onRegisterLogin(): void {
    this.router.navigate(['/accounts/check']);
  }

  public onNewsletterNextStep(): void {
    this.router.navigate(['/accounts/caution'], {
      queryParamsHandling: 'preserve',
    });
  }

  public onCautionNextStep(): void {
    this.route.queryParams.pipe(first()).subscribe((params) => {
      const skipEmailVerification = params?.skip && JSON.parse(params.skip);

      if (skipEmailVerification) {
        this.router.navigate(['/onboarding/details'], {
          queryParams: { skip: true },
        });
      } else {
        this.router.navigate(['/accounts/verify']);
      }
    });
  }

  public onNotValidatedEmail(): Observable<UrlTree> {
    return of(this.router.parseUrl('/accounts/verify'));
  }

  public onVerifyGoToLogin(): void {
    this.router.navigate(['/accounts/check']);
  }

  public onEmailActivation(): Observable<UrlTree> {
    return of(
      this.router.parseUrl(
        '/accounts/check?returnUrl=%onboarding%2Fdownload%2F'
      )
    );
  }

  public onSkipEmailActivation(): Observable<UrlTree> {
    return of(this.router.parseUrl('/onboarding/details'));
  }

  public onDownloadAfterCheckSkipClick(): void {
    this.router.navigate(['/onboarding/details']);
  }

  public onAlreadyActivitedEmailGoToLogin(): void {
    this.router.navigate(['/accounts/check']);
  }

  public onLoginRegister(): void {
    this.router.navigate(['/accounts/create']);
  }

  public onLoginSuccessfully(returnUrl: string): void {
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl, {
        replaceUrl: true,
      });
    } else {
      this.router.navigate(['/dashboard'], { replaceUrl: true });
    }
  }

  public onCreateCustomerSuccessfully(): void {
    this.questionnaireService.saveQuestionnaire().subscribe((saved) => {
      if (saved) {
        this.accountService.resend().pipe(first()).subscribe();
      }
    });
    this.router.navigate(['/onboarding/complete']);
  }

  public onCompleteCloseClick(): void {
    this.router.navigate(['/onboarding/track']);
  }

  public onTrackSkipClick(): void {
    this.router.navigate(['/dashboard']);
  }

  public onCustomerNotExists(): Observable<UrlTree> {
    return of(this.router.parseUrl('/onboarding/details'));
  }

  public onCustomerExists(): Observable<UrlTree> {
    return of(this.router.parseUrl('/dashboard'));
  }

  public onUserNotExists(): Observable<UrlTree> {
    return of(this.router.parseUrl('/accounts/check'));
  }

  public onCustomerWelcomeExists(): Observable<boolean | UrlTree> {
    return of(this.router.parseUrl('/dashboard'));
  }

  public onFallback(): void {
    Sentry.captureException(`[INTENT error] Using fallback for missing intent`);
    this.router.navigate(['/dashboard']); 
  }
}
